import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { STATUS, statusLabel } from "@/lib/format";
import { Inbox, MoreHorizontal, X, Check, ChevronRight } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/* ── Status ─────────────────────────────────────────────────────────────── */
export const StatusBadge = ({ status, className, showIcon = true, label, ...props }) => {
  const s = STATUS[status] || { label: statusLabel(status), cls: "bg-slate-50 text-slate-600 border-slate-200" };
  const Icon = s.icon;
  return (
    <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium whitespace-nowrap", s.cls, className)} data-testid={`status-${status}`} {...props}>
      {showIcon && Icon && <Icon size={11} aria-hidden className="shrink-0" />}{label || s.label}
    </span>
  );
};

/* ── Page chrome ────────────────────────────────────────────────────────── */
export const PageHeader = ({ title, subtitle, actions, crumbs }) => (
  <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
    <div className="min-w-0">
      {crumbs && <Breadcrumbs crumbs={crumbs} />}
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 font-heading truncate" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-slate-600 mt-0.5">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
  </div>
);

export const Breadcrumbs = ({ crumbs }) => (
  <nav className="text-xs text-slate-500 mb-1 flex items-center gap-1 flex-wrap" aria-label="Breadcrumb" data-testid="breadcrumbs">
    {crumbs.filter(Boolean).map((c, i) => (
      <span key={i} className="flex items-center gap-1">
        {i > 0 && <ChevronRight size={11} className="text-slate-300" aria-hidden />}
        {c.to ? <Link className="hover:underline hover:text-slate-700" to={c.to}>{c.label}</Link> : <span className="text-slate-700 font-medium" aria-current="page">{c.label}</span>}
      </span>
    ))}
  </nav>
);

/* ── Metrics: a tile with nothing in it teaches people to ignore the row ── */
export const Kpi = ({ label, value, tone, icon: Icon, onClick, hint, testId, hideWhenZero = false }) => {
  if (hideWhenZero && (value === 0 || value === "0" || value == null || value === "0%")) return null;
  const tones = { red: "text-red-700", amber: "text-amber-800", sky: "text-sky-700", green: "text-emerald-700", slate: "text-slate-900", purple: "text-purple-700" };
  const Comp = onClick ? "button" : "div";
  return (
    <Comp onClick={onClick} className={cn("kpi text-left", onClick && "hover:border-sky-400 hover:shadow transition-shadow cursor-pointer")} data-testid={testId || `kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <span className="kpi-label flex items-center gap-1">{Icon && <Icon size={12} aria-hidden />} {label}</span>
      <span className={cn("kpi-value", tones[tone] || tones.slate)}>{value ?? "—"}</span>
      {hint && <span className="text-[11px] text-slate-500 truncate">{hint}</span>}
    </Comp>
  );
};

export const KpiStrip = ({ children, cols = 6 }) => <div className={cn("grid gap-2 mb-4", { 3: "grid-cols-1 sm:grid-cols-3", 6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6", 5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5", 4: "grid-cols-2 lg:grid-cols-4", 7: "grid-cols-2 sm:grid-cols-4 lg:grid-cols-7", 8: "grid-cols-2 sm:grid-cols-4 lg:grid-cols-8" }[cols])}>{children}</div>;

/* ── Panel: one level of chrome only, with a real heading ───────────────── */
export const Panel = ({ title, actions, children, className, testId, padded = false, level = 2, hint }) => {
  const H = `h${Math.min(4, Math.max(2, level))}`;
  return (
    <section className={cn("panel min-w-0", className)} data-testid={testId} aria-label={typeof title === "string" ? title : undefined}>
      {(title || actions) && (
        <div className="panel-header gap-2">
          <div className="min-w-0">
            <H className="panel-title truncate">{title}</H>
            {hint && <p className="text-[11px] text-slate-500 font-normal">{hint}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 flex-wrap shrink-0 min-w-0">{actions}</div>}
        </div>
      )}
      <div className={padded ? "p-4" : ""}>{children}</div>
    </section>
  );
};

export const EmptyState = ({ title = "Nothing here yet", hint, icon: Icon = Inbox, action }) => (
  <div className="flex flex-col items-center justify-center text-center py-10 px-4 text-slate-500" data-testid="empty-state">
    <Icon size={28} className="mb-2 text-slate-300" aria-hidden />
    <p className="text-sm font-medium text-slate-700">{title}</p>
    {hint && <p className="text-xs mt-1 max-w-sm text-slate-500">{hint}</p>}
    {action && <div className="mt-3">{action}</div>}
  </div>
);

export const ProgressBar = ({ value, className, tone = "sky" }) => (
  <div className={cn("h-1.5 w-full bg-slate-200 rounded-sm overflow-hidden", className)} role="progressbar" aria-valuenow={Math.round(value || 0)} aria-valuemin={0} aria-valuemax={100}>
    <div className={cn("h-full rounded-sm transition-[width] duration-300", { sky: "bg-sky-600", green: "bg-emerald-600", red: "bg-red-600", amber: "bg-amber-500", brand: "bg-brand" }[tone])} style={{ width: `${Math.min(100, value || 0)}%` }} />
  </div>
);

/* ── Tables: desktop rows, mobile summary cards ─────────────────────────── */
export const RowActions = ({ actions, label = "More actions", testId }) => {
  const items = (actions || []).filter(Boolean);
  if (!items.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button onClick={(e) => e.stopPropagation()} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-slate-100 text-slate-500" aria-label={label} data-testid={testId}><MoreHorizontal size={15} /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 bg-white">
        {items.map((a, i) => <DropdownMenuItem key={i} onClick={(e) => { e.stopPropagation(); a.onClick(); }} className={cn(a.destructive && "text-red-700 focus:text-red-700")} data-testid={a.testId}>{a.icon && <a.icon size={14} className="mr-2" />}{a.label}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const DataTable = ({ columns, rows, keyField = "id", onRowClick, empty = "No records", emptyState, testId, dense = true, footer, mobileCard }) => {
  const cols = columns.filter(Boolean);
  const emptyBlock = emptyState || <EmptyState title={typeof empty === "string" ? empty : "No records"} />;
  return (
    <div data-testid={testId}>
      {/* desktop */}
      <div className={cn("overflow-x-auto", mobileCard && "hidden md:block")}>
        <table className="dense-table">
          <thead><tr>{cols.map((c) => <th key={c.key} scope="col" className={c.className} style={c.width ? { width: c.width } : undefined}>{c.label}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={cols.length} className="py-2">{emptyBlock}</td></tr>}
            {rows.map((r, i) => (
              <tr key={r[keyField] ?? i} onClick={onRowClick ? () => onRowClick(r) : undefined} className={cn(onRowClick && "cursor-pointer")} tabIndex={onRowClick ? 0 : undefined} onKeyDown={onRowClick ? (e) => e.key === "Enter" && onRowClick(r) : undefined} data-testid={`row-${r[keyField] ?? i}`}>
                {cols.map((c) => <td key={c.key} className={cn(c.className, !dense && "py-2.5")}>{c.render ? c.render(r) : r[c.key] ?? "—"}</td>)}
              </tr>))}
          </tbody>
          {footer && <tfoot><tr>{footer}</tr></tfoot>}
        </table>
      </div>
      {/* mobile: a wide admin table is unusable on a phone, so render a summary card per record */}
      {mobileCard && (
        <div className="md:hidden divide-y divide-slate-100" data-testid={testId ? `${testId}-mobile` : undefined}>
          {rows.length === 0 && emptyBlock}
          {rows.map((r, i) => (
            <div key={r[keyField] ?? i} onClick={onRowClick ? () => onRowClick(r) : undefined} className={cn("p-3", onRowClick && "cursor-pointer active:bg-slate-50")} data-testid={`mobile-row-${r[keyField] ?? i}`} role={onRowClick ? "button" : undefined} tabIndex={onRowClick ? 0 : undefined} onKeyDown={onRowClick ? (e) => e.key === "Enter" && onRowClick(r) : undefined}>
              {mobileCard(r)}
            </div>))}
        </div>
      )}
    </div>
  );
};

/* ── Filters: an applied filter the user cannot see is a bug ────────────── */
export const FilterBar = ({ children, active = [], onClear, testId = "filter-bar" }) => {
  const chips = active.filter((a) => a && a.value);
  return (
    <div className="px-4 py-2.5 border-b border-slate-200 space-y-2" data-testid={testId}>
      <div className="flex flex-wrap items-end gap-2">{children}</div>
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="active-filters">
          <span className="text-[11px] text-slate-500 font-medium">Filtered by</span>
          {chips.map((a, i) => (
            <span key={i} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-sky-50 border border-sky-200 text-sky-800 text-[11px]">
              {a.label}: <strong className="font-semibold">{a.display || a.value}</strong>
              {a.onRemove && <button onClick={a.onRemove} className="hover:bg-sky-100 rounded-full p-0.5" aria-label={`Remove ${a.label} filter`} data-testid={`clear-filter-${a.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><X size={11} /></button>}
            </span>))}
          {onClear && <button onClick={onClear} className="text-[11px] text-slate-600 underline hover:text-slate-900 ml-1" data-testid="clear-filters-btn">Clear all</button>}
        </div>
      )}
    </div>
  );
};

/* ── Steppers for guided work (course builder, migration journey) ───────── */
export const Stepper = ({ steps, current, onSelect, testId = "stepper" }) => (
  <nav className="flex items-stretch gap-1 overflow-x-auto border border-slate-200 bg-white rounded-md p-1 mb-4" aria-label="Progress" data-testid={testId}>
    {steps.map((s, i) => {
      const active = s.key === current;
      return (
        <button key={s.key} onClick={() => onSelect?.(s.key)} aria-current={active ? "step" : undefined} disabled={!onSelect || s.disabled}
          className={cn("flex items-center gap-2 px-2.5 py-1.5 rounded text-[13px] whitespace-nowrap transition-colors min-w-0",
            active ? "bg-slate-900 text-white font-semibold" : s.disabled ? "text-slate-400" : "text-slate-600 hover:bg-slate-100")}
          data-testid={`step-${s.key}`}>
          <span className={cn("h-4 w-4 rounded-full border text-[10px] flex items-center justify-center shrink-0 font-semibold",
            s.done ? "bg-emerald-600 border-emerald-600 text-white" : s.warn ? "bg-amber-100 border-amber-400 text-amber-900" : active ? "border-white/60 text-white" : "border-slate-300 text-slate-500")}>
            {s.done ? <Check size={10} /> : i + 1}
          </span>
          <span className="truncate">{s.label}</span>
        </button>);
    })}
  </nav>
);

/* ── Loading: match the shape of what is coming so the page cannot jump ── */
const Bar = ({ className }) => <div className={cn("bg-slate-200/80 rounded animate-pulse", className)} />;

export const SkeletonTable = ({ rows = 6, cols = 5, testId = "skeleton-table" }) => (
  <div className="p-3 space-y-2" data-testid={testId} role="status" aria-label="Loading records">
    <Bar className="h-7 w-full" />
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="flex gap-2">{Array.from({ length: cols }).map((_, c) => <Bar key={c} className={cn("h-5", c === 0 ? "flex-[2]" : "flex-1")} />)}</div>
    ))}
  </div>
);

export const SkeletonKpis = ({ count = 4 }) => (
  <div className="grid gap-2 mb-4 grid-cols-2 lg:grid-cols-4" aria-hidden>{Array.from({ length: count }).map((_, i) => <Bar key={i} className="h-16" />)}</div>
);

export const SkeletonPage = ({ kpis = 4, rows = 6, cols = 5, testId = "skeleton-page" }) => (
  <div data-testid={testId} role="status" aria-label="Loading page">
    <Bar className="h-7 w-56 mb-1" /><Bar className="h-4 w-80 mb-4" />
    {kpis > 0 && <SkeletonKpis count={kpis} />}
    <div className="panel"><SkeletonTable rows={rows} cols={cols} testId={undefined} /></div>
  </div>
);

export const SkeletonCards = ({ count = 6 }) => (
  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" role="status" aria-label="Loading courses">
    {Array.from({ length: count }).map((_, i) => <div key={i} className="panel overflow-hidden"><Bar className="aspect-[16/7] rounded-none" /><div className="p-3 space-y-2"><Bar className="h-4 w-3/4" /><Bar className="h-3 w-full" /><Bar className="h-3 w-1/2" /></div></div>)}
  </div>
);

/* ── Forms ──────────────────────────────────────────────────────────────── */
export const Field = ({ label, children, hint, className, error, required }) => (
  <label className={cn("block", className)}>
    <span className="field-label">{label}{required && <span className="text-red-600 ml-0.5" aria-hidden>*</span>}</span>
    {children}
    {hint && !error && <span className="text-[11px] text-slate-500 mt-0.5 block">{hint}</span>}
    {error && <span className="text-[11px] text-red-700 mt-0.5 block" role="alert">{error}</span>}
  </label>
);

export const FormSection = ({ title, hint, children, className, cols = 3 }) => (
  <fieldset className={cn("border-t border-slate-200 pt-3 mt-4 first:border-0 first:pt-0 first:mt-0", className)}>
    <legend className="sr-only">{title}</legend>
    <div className="mb-2"><div className="eyebrow">{title}</div>{hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}</div>
    <div className={cn("grid gap-3", { 1: "", 2: "md:grid-cols-2", 3: "md:grid-cols-3" }[cols])}>{children}</div>
  </fieldset>
);

export const inputCls = "w-full h-9 px-2.5 text-sm border border-slate-300 rounded-md bg-white focus:border-sky-500 outline-none disabled:bg-slate-50 disabled:text-slate-500";
export const selectCls = inputCls;
export const textareaCls = "w-full px-2.5 py-2 text-sm border border-slate-300 rounded-md bg-white focus:border-sky-500 outline-none min-h-[80px]";

/* ── Buttons: exactly four variants ─────────────────────────────────────── */
export const btn = {
  primary: "h-9 px-3 rounded-md btn-primary text-sm font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50",
  secondary: "h-9 px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-700 inline-flex items-center justify-center gap-1.5 hover:bg-slate-50 disabled:opacity-50",
  destructive: "h-9 px-3 rounded-md bg-red-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-red-700 disabled:opacity-50",
  ghost: "h-8 px-2 rounded-md text-sm text-slate-600 inline-flex items-center justify-center gap-1.5 hover:bg-slate-100 disabled:opacity-50",
  small: "h-7 px-2 rounded border border-slate-300 bg-white text-xs text-slate-700 inline-flex items-center gap-1 hover:bg-slate-50 disabled:opacity-50",
};

export const Spinner = ({ label = "Loading…" }) => <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center" role="status"><span className="h-4 w-4 border-2 border-slate-300 border-t-sky-600 rounded-full animate-spin" aria-hidden />{label}</div>;

/* ── Alerts ─────────────────────────────────────────────────────────────── */
export const Alert = ({ tone = "info", title, children, className, testId }) => {
  const tones = {
    info: "bg-sky-50 border-sky-200 text-sky-900", warning: "bg-amber-50 border-amber-200 text-amber-900",
    danger: "bg-red-50 border-red-200 text-red-800", success: "bg-emerald-50 border-emerald-200 text-emerald-900",
  };
  return (
    <div className={cn("border rounded-md px-3 py-2 text-sm", tones[tone], className)} role={tone === "danger" ? "alert" : "status"} data-testid={testId}>
      {title && <div className="font-semibold mb-0.5">{title}</div>}
      {children}
    </div>
  );
};
